import polyLogoGlb from 'assets/glb/PolyLogo.glb';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

const DESIRED_SIZE = 12;
const ROTATION_SPEED = -0.11;
const CUBE_CAMERA_UPDATE_DIST = 80;
const CUBE_CAMERA_UPDATE_INTERVAL = 10;

/**
 * BNB logo at galaxy center (separate scene, no bloom, live reflections).
 * Ported from: hooks/useGalaxyCore.ts
 */
export class GalaxyCoreSystem {
  private logoScene: THREE.Scene;
  private logo: THREE.Object3D | null = null;
  private cubeCamera: THREE.CubeCamera;
  private cubeRT: THREE.WebGLCubeRenderTarget;
  private goldMaterial: THREE.MeshPhysicalMaterial;
  private elapsed = 0;
  private frameCount = 0;
  private needsEnvRefresh = false;

  constructor(
    private mainScene: THREE.Scene,
    private camera: THREE.PerspectiveCamera,
    private renderer: THREE.WebGLRenderer
  ) {
    // Separate scene for logo (no bloom)
    this.logoScene = new THREE.Scene();
    this.logoScene.background = null;

    // Lighting (from b6.html, unchanged)
    this.logoScene.add(new THREE.AmbientLight(0xffffff, 0.2));

    const keyLight = new THREE.DirectionalLight(0xffffff, 4.0);
    keyLight.position.set(4, 6, 5);
    this.logoScene.add(keyLight);

    const fillLight = new THREE.DirectionalLight(0xffe8b0, 2.0);
    fillLight.position.set(-5, -2, -3);
    this.logoScene.add(fillLight);

    const rimLight = new THREE.DirectionalLight(0xffffff, 3.0);
    rimLight.position.set(0, -4, 6);
    this.logoScene.add(rimLight);

    const topLight = new THREE.DirectionalLight(0xfff4dd, 2.5);
    topLight.position.set(-2, 8, -2);
    this.logoScene.add(topLight);

    // CubeCamera for live reflections (256 — reduced from 512, visually identical)
    this.cubeRT = new THREE.WebGLCubeRenderTarget(256, {
      generateMipmaps: true,
      minFilter: THREE.LinearMipmapLinearFilter,
    });
    this.cubeCamera = new THREE.CubeCamera(0.5, 500, this.cubeRT);
    this.cubeCamera.position.set(0, 0, 0);
    this.mainScene.add(this.cubeCamera);

    // Gold material — matches original
    this.goldMaterial = new THREE.MeshPhysicalMaterial({
      color: 0xf0b90b,
      metalness: 1.0,
      roughness: 0.08,
      envMap: this.cubeRT.texture,
      envMapIntensity: 6.0,
      clearcoat: 0.0,
      clearcoatRoughness: 0.0,
    });

    this.loadLogo();
  }

  /** Schedule a CubeCamera refresh on the next animate() — call after scene content changes (e.g. stars loaded). */
  refreshEnvMap(): void {
    this.needsEnvRefresh = true;
  }

  animate(dt: number): void {
    if (!this.logo) return;

    this.elapsed += dt;
    this.frameCount++;

    this.logo.rotation.y = this.elapsed * ROTATION_SPEED;

    // One-shot refresh when scene content changed (stars loaded, etc.)
    if (this.needsEnvRefresh) {
      this.needsEnvRefresh = false;
      this.cubeCamera.update(this.renderer, this.mainScene);
    }

    // Update CubeCamera only when camera is near (optimization from b6.html)
    const distToCore = this.camera.position.length();
    if (
      distToCore < CUBE_CAMERA_UPDATE_DIST &&
      this.frameCount % CUBE_CAMERA_UPDATE_INTERVAL === 0
    ) {
      this.cubeCamera.update(this.renderer, this.mainScene);
    }
  }

  /** Called AFTER composer.render() — renders logo without bloom */
  render(): void {
    if (!this.logo || !this.logo.visible) return;

    const prevAutoClear = this.renderer.autoClear;
    this.renderer.autoClear = false;
    this.renderer.render(this.logoScene, this.camera);
    this.renderer.autoClear = prevAutoClear;
  }

  destroy(): void {
    this.mainScene.remove(this.cubeCamera);
    this.cubeRT.dispose();
    this.goldMaterial.dispose();

    this.logoScene.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        (child as THREE.Mesh).geometry.dispose();
      }
    });
  }

  private loadLogo(): void {
    const loader = new GLTFLoader();

    const setupLogo = (model: THREE.Object3D) => {
      const box = new THREE.Box3().setFromObject(model);
      const size = new THREE.Vector3();
      box.getSize(size);
      const maxDim = Math.max(size.x, size.y, size.z);
      const scaleFactor = DESIRED_SIZE / maxDim;
      model.scale.setScalar(scaleFactor);

      const center = new THREE.Vector3();
      box.getCenter(center);
      model.position.sub(center.multiplyScalar(scaleFactor));

      model.traverse((child) => {
        if ((child as THREE.Mesh).isMesh) {
          (child as THREE.Mesh).material = this.goldMaterial;
        }
      });

      this.logo = model;
      this.logoScene.add(model);

      // Force initial CubeCamera capture
      this.cubeCamera.update(this.renderer, this.mainScene);
    };

    loader.load(
      polyLogoGlb,
      (gltf) => setupLogo(gltf.scene),
      undefined,
      () => {
        const fallbackGeo = new THREE.OctahedronGeometry(3, 2);
        const fallback = new THREE.Mesh(fallbackGeo, this.goldMaterial);
        setupLogo(fallback);
      }
    );
  }
}
